<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { getCurrentStage, getStageIndex } from '@/utils/statusPipeline'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'

const props = defineProps({
  rootType: {
    type: String,
    required: true
  },
  rootItem: {
    type: Object,
    default: null
  },
  componentMap: {
    type: Object,
    required: true
  },
  stages: {
    type: Array,
    default: null
  },
  ocultarParedes: {
    type: Boolean,
    default: false
  },
  // corEtapaAtiva: cor de fundo da etapa ativa (animação do ícone) nos forms filhos.
  // Repassada via pipelineProps para o ComexPipelineStatus.
  // Quando não fornecida, o pipeline usa a cor da própria etapa (comportamento legado).
  corEtapaAtiva: {
    type: String,
    default: null
  },
  contextoPai: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['cancelar', 'item-atualizado', 'show-notification', 'changesDetected'])

const stack = ref([])
const hasChanges = ref(false)
const pendingNavigationAction = ref(null)
const confirmationModalRef = ref(null)

const initializeStack = () => {
  const rootConfig = props.componentMap[props.rootType]
  if (rootConfig) {
    const rootId = props.rootItem
      ? (props.rootItem.idContratoVenda || props.rootItem.idContratoCompra || props.rootItem.idEmbarque || props.rootItem.idPurchaseOrder || props.rootItem.idCotaCompra || props.rootItem.id)
      : null
    stack.value = [
      {
        type: props.rootType,
        component: rootConfig.component,
        color: rootConfig.color,
        icon: rootConfig.icon,
        title: rootConfig.title,
        id: rootId,
        props: { itemParaEditar: props.rootItem, contextoPai: props.contextoPai }
      }
    ]
  }
}

watch(() => props.rootItem, () => {
  // Só inicializa a pilha se estiver vazia (primeira montagem).
  // handleItemAtualizado já atualiza o item no nível ativo do stack,
  // então não precisamos reinicializar a pilha quando rootItem muda.
  // Reinicializar a pilha aqui destruiria a navegação do drilldown
  // porque o watch pode disparar antes do drilldown-push ser processado.
  if (stack.value.length === 0) {
    initializeStack()
  }
}, { immediate: true })

const activeLevel = computed(() => stack.value[stack.value.length - 1])
const collapsedLevels = computed(() => stack.value.slice(0, stack.value.length - 1))

// Mantém um mapa dos IDs ativos para o Pipeline (dinâmico baseado no componentMap)
const activeIds = computed(() => {
  const ids = {}
  Object.keys(props.componentMap).forEach(key => { ids[key] = null })
  stack.value.forEach(level => {
    if (ids[level.type] !== undefined) {
      ids[level.type] = level.id
    }
  })
  return ids
})

// Pipeline props dinâmico para passar aos componentes filhos (inclui stages e todos os IDs)
const pipelinePropsValue = computed(() => {
  const p = {
    // etapaSelecionada: qual etapa o usuário está visualizando no drilldown (navegação).
    // Pode ser diferente de etapaAtiva quando o usuário navega para uma etapa já concluída.
    etapaSelecionada: activeLevel.value?.type,
    stages: props.stages,
    corEtapaAtiva: props.corEtapaAtiva
  }
  for (const key of Object.keys(activeIds.value)) {
    const propKey = 'id' + key.charAt(0).toUpperCase() + key.slice(1)
    p[propKey] = activeIds.value[key]
  }
  // GUARDIÃO: Normaliza o idEmbarque para as etapas que representam o mesmo embarque
  // (materiais, validacao e embarque apontam para o mesmo registro Embarque).
  // Isso garante que o ComexPipelineStatus e os filhos sempre recebam idEmbarque,
  // independente do root-type ativo no drilldown.
  const idMateriais = p.idMateriais
  const idValidacao = p.idValidacao
  const idEmbarqueProp = p.idEmbarque
  if (idMateriais != null && idEmbarqueProp == null) p.idEmbarque = idMateriais
  if (idValidacao != null && idEmbarqueProp == null) p.idEmbarque = idValidacao

  // GUARDIÃO: Propaga o lstStatusEmbarque do item ativo para o pipeline
  // Isso garante que o ComexPipelineStatus e os forms filhos saibam o status real
  // do backend, mesmo quando navegam para trás (read-only).
  const itemAtivo = activeLevel.value?.props?.itemParaEditar
  if (itemAtivo?.lstStatusEmbarque) {
    p.lstStatusEmbarque = itemAtivo.lstStatusEmbarque
  }

  // ============================================================
  // etapaAtiva: etapa correspondente ao status REAL do backend.
  // Quando lstStatusEmbarque existe (fluxo de embarques), deriva do backend.
  // Quando não existe (fluxo de contratos - etapas independentes), usa a
  // etapa selecionada (comportamento legado).
  // ============================================================
  if (p.lstStatusEmbarque) {
    p.etapaAtiva = getCurrentStage(p.lstStatusEmbarque)
  } else {
    p.etapaAtiva = activeLevel.value?.type
  }

  // ============================================================
  // stageStatus: Array de estados para cada etapa da pipeline
  // Calculado a partir do lstStatusEmbarque real do backend.
  // Regras:
  //   - completed: etapas anteriores ao status atual
  //   - active: etapa correspondente ao status atual
  //   - available: próxima etapa imediatamente após a atual
  //   - inactive: etapas futuras (2+ passos à frente)
  // ============================================================
  if (props.stages && props.stages.length > 0 && p.lstStatusEmbarque) {
    const currentStageKey = getCurrentStage(p.lstStatusEmbarque)
    const currentIdx = getStageIndex(currentStageKey)
    if (currentIdx !== -1) {
      p.stageStatus = props.stages.map((_, index) => {
        if (index < currentIdx) return 'completed'
        if (index === currentIdx) return 'active'
        if (index === currentIdx + 1) return 'available'
        return 'inactive'
      })
    }
  }

  // ============================================================
  // readOnly: indica que a etapa selecionada NÃO é a etapa ativa do backend.
  // Usado pelos forms de embarque para ocultar a toolbar e exibir em modo
  // visualização (etapas já ultrapassadas só podem ser visualizadas).
  // No fluxo de contratos (sem lstStatusEmbarque) sempre é false.
  // ============================================================
  p.readOnly = !!(p.lstStatusEmbarque && p.etapaSelecionada && p.etapaAtiva && p.etapaSelecionada !== p.etapaAtiva)

  return p
})

const popToLevel = (index) => {
  if (index >= 0 && index < stack.value.length - 1) {
    // GUARDIÃO: Cria um novo array em vez de usar splice() para garantir
    // que a reatividade do Vue 3 detecte a mudança no ref().
    stack.value = stack.value.slice(0, index + 1)
  }
}

const executarPopToLevel = (index) => {
  if (hasChanges.value) {
    pendingNavigationAction.value = () => {
      hasChanges.value = false
      popToLevel(index)
    }
    confirmationModalRef.value?.show()
  } else {
    popToLevel(index)
  }
}

const handlePush = (payload) => {
  const config = props.componentMap[payload.type]
  if (config) {
    stack.value.push({
      type: payload.type,
      component: config.component,
      color: config.color,
      icon: config.icon,
      title: payload.title || config.title,
      id: payload.id,
      props: payload.props || { itemParaEditar: payload.item || null }
    })
  }
}

const executarPipelineNavigation = (etapaAlvo) => {
  hasChanges.value = false

  // Acha no stack onde está a etapaAlvo
  const index = stack.value.findIndex(s => s.type === etapaAlvo)
  if (index !== -1) {
    popToLevel(index)
    return
  }

  const stages = props.stages
  if (stages && stages.length > 0) {
    const currentIdx = stages.findIndex(s => s.key === activeLevel.value?.type)
    const targetIdx = stages.findIndex(s => s.key === etapaAlvo)

    // Navegação para TRÁS: etapa anterior na pipeline que não está na pilha
    if (currentIdx !== -1 && targetIdx !== -1 && targetIdx < currentIdx) {
      const config = props.componentMap[etapaAlvo]
      if (config) {
        const currentItem = activeLevel.value?.props?.itemParaEditar || props.rootItem

        const idEmbarque = currentItem?.idEmbarque || currentItem?.id || activeIds.value.idEmbarque || null
        stack.value = [{
          type: etapaAlvo,
          component: config.component,
          color: config.color,
          icon: config.icon,
          title: config.title,
          id: idEmbarque,
          props: { itemParaEditar: currentItem }
        }]
      }
      return
    }

    // Navegação para FRENTE para etapa já concluída (fluxo de embarques):
    if (targetIdx !== -1 && pipelinePropsValue.value.stageStatus) {
      const targetStatus = pipelinePropsValue.value.stageStatus[targetIdx]
      if (targetStatus === 'completed' || targetStatus === 'active') {
        const config = props.componentMap[etapaAlvo]
        if (config) {
          const currentItem = activeLevel.value?.props?.itemParaEditar || props.rootItem
          const idEmbarque = currentItem?.idEmbarque || currentItem?.id || activeIds.value.idEmbarque || null
          stack.value = [{
            type: etapaAlvo,
            component: config.component,
            color: config.color,
            icon: config.icon,
            title: config.title,
            id: idEmbarque,
            props: { itemParaEditar: currentItem }
          }]
        }
        return
      }
    }

    // Navegação para frente: descobre se a etapa alvo é a próxima na pipeline configurada
    if (currentIdx !== -1 && targetIdx === currentIdx + 1) {
      // O form atual deve emitir drilldown-push para navegar para a próxima etapa
    }
  }
}

const handlePipelineNavigation = (etapaAlvo) => {
  if (hasChanges.value) {
    pendingNavigationAction.value = () => executarPipelineNavigation(etapaAlvo)
    confirmationModalRef.value?.show()
  } else {
    executarPipelineNavigation(etapaAlvo)
  }
}

const onConfirmNavigation = (confirmed) => {
  if (confirmed && pendingNavigationAction.value) {
    pendingNavigationAction.value()
  }
  pendingNavigationAction.value = null
}

const handleCancel = () => {
  // GUARDIÃO: Sempre propaga cancelar para fechar o painel lateral no dashboard,
  // independente do nível do drilldown. Isso garante que o usuário volte
  // diretamente para a tela de administração (lista) ao clicar em Cancelar.
  emit('cancelar')
}

const handleItemAtualizado = (savedItem) => {
  // Atualiza o ID do item recém salvo no stack atual
  if (activeLevel.value) {
    // Caso especial para PurchaseOrder que usa idPurchaseOrder, Embarque usa idEmbarque, etc.
    let newId = savedItem.id
    if (activeLevel.value.type === 'contrato') newId = savedItem.idContratoVenda || savedItem.idContratoCompra || savedItem.id
    else if (activeLevel.value.type === 'purchaseOrder' || activeLevel.value.type === 'cota') newId = savedItem.idPurchaseOrder || savedItem.idCotaCompra || savedItem.idCota || savedItem.id
    else if (activeLevel.value.type === 'embarque') newId = savedItem.idEmbarque || savedItem.id
    else if (activeLevel.value.type === 'materiais' || activeLevel.value.type === 'validacao') newId = savedItem.idEmbarque || savedItem.id

    // Cria um NOVO objeto de nível para forçar a reavaliação do computed activeLevel
    // Mutar activeLevel.value.props.itemParaEditar não dispara recálculo do computed
    const novoNivel = {
      ...activeLevel.value,
      id: newId || activeLevel.value.id,
      props: { ...activeLevel.value.props, itemParaEditar: savedItem }
    }
    const index = stack.value.length - 1
    stack.value[index] = novoNivel
  }

  if (stack.value.length === 1) {
    emit('item-atualizado', savedItem)
  }
}

onMounted(() => {
  // Log inicial silencioso para diagnóstico
})
</script>

<template>
  <div class="comex-drilldown-container d-flex h-100 w-100" data-testid="drilldown-container">
    <!-- Paredes colapsadas (ocultas quando ocultarParedes=true) -->
    <transition-group v-if="!ocultarParedes" name="wall" tag="div" class="drilldown-walls-wrapper d-flex">
      <div
        v-for="(level, index) in collapsedLevels"
        :key="level.type + (level.id || index)"
        class="drilldown-wall cursor-pointer d-flex flex-column align-items-center justify-content-center py-3"
        :class="{ 'wall-warning': level.color === 'var(--bs-warning)' }"
        :style="{ backgroundColor: level.color }"
        @click="executarPopToLevel(index)"
        :title="`Voltar para ${level.title}`"
      >
        <template v-if="level.icon">
          <!-- GUARDIÃO: O ícone bi-ship não existe nativamente no Bootstrap Icons. Usando Lucide SVG conforme orientação do arquiteto -->
          <svg v-if="level.icon === 'bi-ship'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-ship mb-3 text-white">
            <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
            <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
            <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/>
            <path d="M12 10v4"/>
            <path d="M12 2v3"/>
          </svg>
          <i v-else :class="['bi', level.icon, 'text-white', 'mb-3', 'fs-4']"></i>
        </template>
        <span class="vertical-text text-white fw-bold">{{ level.title }} <span v-if="level.id">#{{ level.id }}</span></span>
      </div>
    </transition-group>

    <!-- Conteúdo Ativo -->
    <div class="drilldown-active-content flex-grow-1 overflow-auto bg-body position-relative">
      <!--
        GUARDIÃO: O <transition> com mode="out-in" foi removido porque causava um bug
        de reatividade: ao navegar de volta via popToLevel, o componente filho não era
        montado. O Vue 3 tem limitações com transições em componentes fragment (múltiplos
        root nodes), e o mode="out-in" impedia a conclusão da transição de saída,
        bloqueando a montagem do novo componente.
      -->
      <component
        :key="activeLevel ? activeLevel.type : 'empty'"
        v-if="activeLevel"
        :is="activeLevel.component"
        :item-para-editar="activeLevel.props.itemParaEditar"
        v-bind="activeLevel.props"
        :drilldown-mode="true"
        :pipeline-props="pipelinePropsValue"
        @drilldown-push="handlePush"
        @drilldown-pop="handleCancel"
        @cancelar="handleCancel"
        @item-atualizado="handleItemAtualizado"
        @pipeline-navegar="handlePipelineNavigation"
        @update:funcao="(v) => { if (activeLevel) activeLevel.props.funcao = v }"
        @show-notification="(n) => emit('show-notification', n)"
        @changes-detected="(v) => { hasChanges = v; emit('changesDetected', v) }"
      />
    </div>
  </div>

  <ConfirmationModal
    ref="confirmationModalRef"
    title="Descartar Alterações?"
    message="Você tem alterações não salvas no formulário atual. Deseja descartá-las e continuar?"
    @confirmed="onConfirmNavigation"
  />
</template>

<style scoped>
.comex-drilldown-container {
  overflow: hidden;
}

.drilldown-wall {
  width: 40px;
  min-width: 40px;
  border-right: 1px solid rgba(255,255,255,0.2);
  cursor: pointer;
  transition: width 0.02S cubic-bezier(0.2, 0.8, 0.2, 1), min-width 0.02S cubic-bezier(0.2, 0.8, 0.2, 1), background-color 0.02S ease;
}

.drilldown-wall:hover {
  filter: brightness(1.1);
  width: 45px;
}

/* Garantir que o SVG do Lucide (ícone ship) herde a cor para ter contornos brancos como os demais ícones */
.drilldown-wall svg.lucide-ship {
  stroke: currentColor;
  color: inherit;
}

/* Ajuste de contraste para o amarelo */
.wall-warning i,
.wall-warning svg,
.wall-warning .vertical-text {
  color: #212529 !important;
}

/* O SVG do Lucide (ícone ship) no wall-warning deve ter contornos brancos como os demais ícones */
.wall-warning svg.lucide-ship {
  color: white !important;
}

/* Transição das Paredes (Drilldown Walls) */
.wall-enter-active,
.wall-leave-active {
  transition: all 0.02S cubic-bezier(0.2, 0.8, 0.2, 1);
  overflow: hidden;
  white-space: nowrap;
}

.wall-enter-from,
.wall-leave-to {
  width: 0 !important;
  min-width: 0 !important;
  opacity: 0;
}

.vertical-text {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  letter-spacing: 2px;
  font-size: 0.9rem;
}

.drilldown-active-content {
  box-shadow: -5px 0 15px rgba(0,0,0,0.05);
  z-index: 10;
  position: relative;
  min-width: 0; /* Importante para telas estreitas - permite shrink */
  max-width: 100%;
  overflow-x: hidden;
}

/* ============================================================ */
/* FIX: Garantir conteúdo ativo em telas estreitas              */
/* ============================================================ */
@media (max-width: 991.98px) {
  .comex-drilldown-container {
    width: 100% !important;
    max-width: 100% !important;
  }

  .drilldown-walls-wrapper {
    flex-shrink: 0;
  }

  .drilldown-active-content {
    flex: 1;
    min-width: 0;
    max-width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }

  /* Garante que elementos filhos não transbordem */
  .drilldown-active-content > * {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
}
</style>
